<template>
  <!--
    QR Service — customer-facing shell rendered at /service/:token.
    Mobile-first; the bottom tab bar is the primary navigation since
    customers reach this page by scanning a printed QR at their table.
  -->
  <div class="service-shell d-flex flex-column bg-light" :class="{ 'is-scrolled': headerCollapsed }" style="min-height: 100vh;">

    <!--
      Top bar: brand + language switcher.
      Table/seat/zone info used to live here but came from mock data —
      no public endpoint exposes it without spending an extra round-trip,
      and TabOrders already surfaces the real table name via /my-order.
      So this stays static; customer context lives inside each tab.
    -->
    <header class="align-content-center bg-theme position-sticky px-3 py-0 py-1 shadow-sm text-white top-0 z-3">
      <div class="d-flex align-items-center justify-content-between gap-2">
        <div class="d-flex align-items-center gap-2" style="min-width: 0;">
          <i class="fa-solid fa-utensils fs-5 flex-shrink-0" />
          <div class="lh-sm" style="min-width: 0;">
            <div class="fw-bold text-truncate">{{ $ln($brand) }}</div>
            <div class="header-sub opacity-75 text-truncate" style="font-size: 0.7rem;">
              <i class="fa-solid fa-qrcode me-1" />{{ $appName }} · {{ $t('QR Service · Order easily from your phone') }}
            </div>
          </div>
        </div>

        <div class="dropdown flex-shrink-0">
          <button
            type="button"
            class="lang-btn"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            :aria-label="$t('Change language')"
          >
            <i class="fa-solid fa-globe" />
            <span>{{ currentLang.label }}</span>
            <i class="fa-solid fa-chevron-down lang-btn__caret" />
          </button>
          <ul class="dropdown-menu dropdown-menu-end shadow lang-menu">
            <li
              v-for="lang in languages"
              :key="lang.code"
            >
              <button
                type="button"
                class="dropdown-item lang-item"
                :class="{ active: currentLocale === lang.code }"
                @click="switchLang(lang.code)"
              >
                <span class="lang-item__flag">{{ lang.flag }}</span>
                <span class="flex-grow-1 text-start">{{ lang.label }}</span>
                <i
                  v-if="currentLocale === lang.code"
                  class="fa-solid fa-check lang-item__check"
                />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>

    <!-- Active tab -->
    <main class="flex-grow-1 pb-5" style="padding-bottom: 90px !important;">
      <component
        :is="tabs[activeTab].component"
        :cart="cart"
        :sending-order="sendingOrder"
        :unavailable-seq="unavailableSeq"
        v-bind="activeTab === 'menu' ? { rewardStatus } : {}"
        @add-to-cart="onAddToCart"
        @update-cart-item="onUpdateCartItem"
        @remove-cart-item="onRemoveCartItem"
        @remove-unavailable="onRemoveUnavailable"
        @clear-cart="cart = []"
        @send-order="onSendOrder"
        @notify="onNotify"
        @switch-tab="activeTab = $event"
        @promo-claims-changed="promotionClaims = $event"
        @rewards="onRewards"
        @ready="onTabReady"
        @session-invalid="onSessionInvalid"
      />
    </main>

    <!-- Sticky bottom tab bar -->
    <nav class="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg z-3 pb-safe">
      <div class="d-flex">
        <button
          v-for="(tab, key) in tabs"
          :key="key"
          class="btn flex-fill py-2 rounded-0 border-0 position-relative"
          :class="activeTab === key ? 'text-theme fw-bold' : 'text-muted'"
          @click="activeTab = key"
        >
          <div class="position-relative d-inline-block">
            <i :class="tab.icon" class="fs-5 d-block" />
            <span
              v-if="key === 'menu' && cartCount > 0"
              class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style="font-size: 0.55rem;"
            >{{ cartCount }}</span>
            <!-- A: จุดบนแท็บบิล เมื่อมีของแถม/รางวัลรอ (แจ้งเฉยๆ ไม่บอกจำนวน —
                 บางอันยอดเพิ่งถึงจากตะกร้ายังกดรับที่หน้าบิลไม่ได้ จนกว่าจะส่งครัว) -->
            <span
              v-if="key === 'bill' && rewardStatus.total > 0"
              class="position-absolute top-0 start-100 translate-middle bg-success rounded-circle border border-2 border-white"
              style="width: 11px; height: 11px;"
            ><span class="visually-hidden">{{ $t('You have rewards waiting') }}</span></span>
          </div>
          <div class="small mt-1">{{ $t(tab.label) }}</div>
        </button>
      </div>
    </nav>

    <!--
      Customer-facing toasts: pill notifications that pop in just under
      the header. Centered, brand-tinted, tap-to-dismiss — designed to
      feel like an in-app confirmation rather than a system alert.
    -->
    <div class="service-toasts">
      <transition-group name="toast-pop" tag="div" class="d-flex flex-column align-items-center gap-2">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="service-toast"
          :class="`service-toast--${t.variant || 'default'}`"
          role="status"
          @click="dismissToast(t.id)"
        >
          <i :class="t.icon || 'fa-solid fa-circle-check'" class="service-toast__icon" />
          <span class="service-toast__msg">{{ t.message }}</span>
        </div>
      </transition-group>
    </div>

    <!--
      First-paint boot screen. This is NOT a skeleton — skeletons (the
      grey/peach content placeholders) are reserved for switching between
      tabs once the session is known to be valid. On the very first visit
      we instead show a branded full-screen loader that covers the shell
      until the active tab confirms the session. Two reasons:
        • Someone opening a mangled /service/<garbage> URL should see a
          neutral "preparing" screen and then be bounced to the public
          menu preview — never a half-built skeleton of a session they
          don't actually have.
        • It reads as the restaurant "getting your table ready" rather
          than the app's internal scaffolding.
      The active tab emits `ready` once its first fetch settles (success
      OR ordinary error), which flips `booted` and fades this away.
    -->
    <transition name="boot-fade">
      <div v-if="!booted" class="service-boot bg-theme">
        <div class="service-boot__inner text-white">
          <div class="service-boot__logo">
            <i class="fa-solid fa-utensils" />
          </div>
          <div class="service-boot__brand">{{ $ln($brand) }}</div>
          <div class="service-boot__dots" aria-hidden="true">
            <span /><span /><span />
          </div>
          <div class="service-boot__hint">{{ $t('Getting things ready…') }}</div>
          <div class="service-boot__hint" style="opacity: 0.55; letter-spacing: 1px;">{{ $appName }}</div>
        </div>
      </div>
    </transition>

    <!--
      Session closed overlay. Shown when the scanned token no longer maps to
      an active session (stale QR left on the table after the previous
      customer's session was closed). Full-screen branded notice that tells
      the customer to contact staff — never falls back to the session shell.
    -->
    <div v-if="sessionClosed" class="service-boot bg-theme">
      <div class="service-boot__inner text-white">
        <div class="service-boot__logo">
          <i class="fa-solid fa-circle-xmark" />
        </div>
        <div class="service-boot__brand">{{ $t('Service has ended') }}</div>
        <div class="service-boot__hint">{{ $t('Please contact our staff') }}</div>
      </div>
    </div>

    <!--
      ระบบของร้านหมดอายุ / แพ็กไม่มีสั่งผ่าน QR — ใช้จอเดียวกับ "ปิดการใช้บริการแล้ว" โดยตั้งใจ
      ลูกค้าที่นั่งอยู่ในร้านควรได้ข้อความที่พาไปหาพนักงาน ไม่ใช่รู้เรื่องบิล/แพ็กระหว่างเรากับร้าน
    -->
    <div v-if="orderingUnavailable" class="service-boot bg-theme">
      <div class="service-boot__inner text-white">
        <div class="service-boot__logo">
          <i class="fa-solid fa-utensils" />
        </div>
        <div class="service-boot__brand">{{ $t('_license.Ordering is unavailable right now') }}</div>
        <div class="service-boot__hint">{{ $t('Please contact our staff') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import TabMenu from './TabMenu.vue';
import TabOrders from './TabOrders.vue';
import TabService from './TabService.vue';
import TabBill from './TabBill.vue';
import TabMore from './TabMore.vue';
import { ServiceOrders, isServiceSessionInvalid } from '@/services/endpoint/services.service';
import { isItemUnavailableError, parseUnavailableItems, groupByItemIndex } from '@/utils/unavailableItems';
import { setLocale } from '@/plugins/i18n';
import { useLicenseStore } from '@/stores/license.store';

export default {
  name: 'CustomerService',
  components: { TabMenu, TabOrders, TabService, TabBill, TabMore },
  data() {
    return {
      // เพิ่มภาษาในอนาคต = เติม object ที่นี่ (code ต้องตรงกับ $i18n.locale)
      languages: [
        { code: 'th', label: 'ไทย', flag: '🇹🇭' },
        { code: 'en', label: 'English', flag: '🇬🇧' },
      ],
      activeTab: 'menu',
      tabs: {
        menu:    { component: 'TabMenu',    icon: 'fa-solid fa-utensils',     label: 'Menu' },
        orders:  { component: 'TabOrders',  icon: 'fa-solid fa-receipt',      label: 'My Orders' },
        service: { component: 'TabService', icon: 'fa-solid fa-bell-concierge', label: 'Service' },
        bill:    { component: 'TabBill',    icon: 'fa-solid fa-file-invoice-dollar', label: 'Bill' },
        // more:    { component: 'TabMore',    icon: 'fa-solid fa-ellipsis',     label: 'More' },
      },
      cart: [],
      // นับจำนวนครั้งที่ส่งไม่ผ่านเพราะของหมด — TabMenu watch ตัวนี้เพื่อเลื่อนไปแถวที่ไฮไลต์
      // ใช้ counter ไม่ใช่ boolean เพราะถ้าส่งซ้ำแล้วพังชุดเดิม ค่า boolean ไม่เปลี่ยน → watcher ไม่ยิง
      unavailableSeq: 0,
      promotionClaims: [],
      // --- reward discoverability (badge บนแท็บบิล + CTA หน้าเมนู + toast) ---
      // container เป็นเจ้าของ summary เดียว เพราะแท็บ mount ทีละตัว (panel ในแท็บบิลไม่อยู่ตอนชอป)
      rewardTiers: [],          // tier โปร (cache จาก ServicePromotion.get) — progress คิดรวมตะกร้าสด
      rewardTierMode: 'single',
      rewardClaimedIds: [],     // claimed_tier_ids จาก server
      loyaltyRedeemable: 0,     // จำนวน reward loyalty ที่แลกได้ (ดู/แจ้งพนักงาน — ไม่ใช่กดเอง)
      // True while POST /orders is in flight — TabMenu reads this to
      // disable the Send button and show a spinner so the customer can't
      // double-fire the request.
      sendingOrder: false,
      // First-paint boot gate. False until the active (first) tab confirms
      // the session by completing its initial fetch — drives the branded
      // full-screen loader. Once true it never flips back, so tab switches
      // fall through to each tab's own skeleton instead of this overlay.
      booted: false,
      // Set true when the session token is dead/closed (stale QR left on the
      // table, or staff closed the session) → show the full-screen "service
      // closed, contact staff" overlay instead of the session shell.
      sessionClosed: false,
      toasts: [],
      _toastSeq: 0,
      // Collapsing header: true once the customer scrolls past the
      // expand/collapse band, hides the subtitle + shrinks the bar so
      // more of the menu is visible. Hysteresis (see _onScroll) keeps it
      // from flickering when the scroll position hovers on the threshold.
      headerCollapsed: false,
      _onScroll: null,
      _scrollRaf: false,
    };
  },
  computed: {
    // ระบบของร้านหมดอายุ หรือแพ็กไม่มีสั่งผ่าน QR — ปิดหน้าสั่งอาหารด้วยข้อความกลาง ๆ
    // ห้ามให้ลูกค้าของร้านเห็นคำว่าหมดอายุ/ค้างชำระ/แพ็กเด็ดขาด (นี่คือเรื่องระหว่างเรากับร้าน)
    orderingUnavailable() {
      const license = useLicenseStore();
      return license.isLocked || license.servicesUnavailable;
    },
    cartCount() {
      return this.cart.reduce((sum, item) => sum + item.quantity, 0);
    },
    cartTotal() {
      return this.cart.reduce((s, i) => s + (Number(i.unit_price) || 0) * i.quantity, 0);
    },
    // มีแถวที่ถูก server ตีกลับว่าสั่งไม่ได้แล้ว → TabMenu โชว์แบนเนอร์ + ปุ่มนำออกแล้วส่งต่อ
    hasUnavailable() {
      return this.cart.some(i => i._unavailable && i._unavailable.length);
    },
    currentLocale() {
      return this.$i18n.locale;
    },
    currentLang() {
      return this.languages.find(l => l.code === this.currentLocale) || this.languages[0];
    },
    // สรุปสถานะ reward (คิดรวมตะกร้าสด) → ขับ badge แท็บบิล + CTA หน้าเมนู
    rewardStatus() {
      const claimed = new Set([
        ...(this.rewardClaimedIds || []).map(Number),
        ...this.promotionClaims.map(c => Number(c.promotion_item_id)),
      ]);
      const usedSingle = this.rewardTierMode === 'single' && claimed.size > 0;

      // โชว์ CTA progress เฉพาะตอน "ใกล้ถึง" (goal-gradient แรงสุดช่วงท้าย) — อีกไกลถือเป็น noise
      const NEAR_SPEND = 75; // บาท
      const NEAR_QTY = 3;    // ชิ้น

      let promoClaimable = 0;
      let nearestRemaining = null;
      let nearestName = '';
      let nearestType = 'spend';
      let nearestPercent = 0;

      if (!usedSingle) {
        for (const t of this.rewardTiers) {
          if (t.reward_in_stock === false) continue;
          const remaining = this.tierRemaining(t);
          if (remaining <= 0) {
            if (!claimed.has(Number(t.promotion_item_id))) promoClaimable++;
            continue;
          }
          // nudge เฉพาะ tier ที่ใกล้ถึงตามชนิดเงื่อนไข
          const isNear = t.condition_type === 'quantity' ? remaining <= NEAR_QTY : remaining <= NEAR_SPEND;
          if (!isNear) continue;
          if (nearestRemaining === null || remaining < nearestRemaining) {
            nearestRemaining = remaining;
            nearestName = t.reward_product_name;
            nearestType = t.condition_type;
            const threshold = Number(t.threshold) || 0;
            nearestPercent = threshold > 0 ? Math.min(100, Math.max(0, Math.round((1 - remaining / threshold) * 100))) : 0;
          }
        }
      }

      const total = promoClaimable + this.loyaltyRedeemable;
      return {
        promoClaimable,
        loyaltyRedeemable: this.loyaltyRedeemable,
        total,
        nearestRemaining: nearestRemaining || 0,
        nearestName,
        nearestType,
        nearestPercent,
      };
    },
  },
  mounted() {
    this._onScroll = () => {
      // rAF throttle: one state check per frame regardless of event burst.
      if (this._scrollRaf) return;
      this._scrollRaf = true;
      requestAnimationFrame(() => {
        this._scrollRaf = false;
        const y = window.scrollY;
        // Hysteresis: collapse past 56px, expand only once near the top
        // again (<16px). The gap stops a single scroll position that sits
        // right on the line from rapidly toggling the header.
        if (!this.headerCollapsed && y > 56) this.headerCollapsed = true;
        else if (this.headerCollapsed && y < 16) this.headerCollapsed = false;
      });
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    // ไม่ refreshRewards ที่นี่ — TabMenu (แท็บแรก) fetch /menu แล้ว relay promotion+loyalty
    // ขึ้นมาผ่าน @rewards ให้เอง (กลับเข้าแท็บ menu = remount = fetch ใหม่ → badge อัปเดต)
  },
  beforeUnmount() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
  },
  methods: {
    switchLang(lang) {
      setLocale(lang);
    },

    // --- Boot / session gate ---------------------------------------------
    // Fired by the active tab once its first fetch settles (ok or error).
    // Lifts the branded boot overlay; only the first call matters.
    onTabReady() {
      this.booted = true;
    },
    // Session token is dead / bogus — commonly a stale QR from a previous
    // customer whose session was already closed. Surface a full-screen
    // "service closed, contact staff" notice instead of exposing the shell.
    onSessionInvalid() {
      this.sessionClosed = true;
      this.booted = true;
    },

    // --- Cart mutations ---------------------------------------------------
    onAddToCart(item) {
      const sig = this._cartSignature(item);
      const existing = this.cart.find(i => this._cartSignature(i) === sig);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        this.cart.push({ ...item, _uid: Date.now() + Math.random() });
      }
      this.pushToast({
        message: this.$t('Added to cart: {product_name}', { product_name: this.$ln(item.variant_name ? { name: item.variant_name, name_en: item.variant_name_en } : item) }),
        variant: 'success',
        icon: 'fa-solid fa-cart-plus',
      });
    },
    onUpdateCartItem({ uid, patch }) {
      const idx = this.cart.findIndex(i => i._uid === uid);
      if (idx === -1) return;
      // แก้แถวแล้ว = ธง "สั่งไม่ได้" รอบก่อนหมดอายุ (เช่นเปลี่ยน option ที่ของหมดออก)
      this.cart.splice(idx, 1, { ...this.cart[idx], ...patch, _unavailable: null });
    },
    onRemoveCartItem(uid) {
      this.cart = this.cart.filter(i => i._uid !== uid);
    },
    _cartSignature(item) {
      // Identical product + variant + modifier set + remark collapse into one cart line.
      return [
        item.product_id,
        item.variant_id || '',
        (item.modifier_option_ids || []).slice().sort().join(','),
        item.remark || '',
      ].join('|');
    },

    // --- Send to kitchen --------------------------------------------------
    /**
     * POST /v1/services/{token}/orders with the cart contents.
     *
     * Mapping rules (per the API contract):
     *   • `product_id` in the payload must be the **variant id** for
     *     Configurable products (variant rows ARE the SKU). Use
     *     `variant_id || product_id`.
     *   • Only the four whitelisted item keys are allowed — adding
     *     `unit_price`, `name`, etc. would 422 via NotAllowedRule.
     *   • The optional top-level `customer_count` is omitted; server
     *     falls back to the session's value.
     *
     * ล้มเหลว = ไม่ล้างตะกร้า ลูกค้าแก้แล้วกดส่งซ้ำได้เลย
     *
     * ของหมดระหว่างถือตะกร้า (ERROR_ITEM_UNAVAILABLE) = เคสที่เจอบ่อยสุด แยกจัดการ:
     * ติดธงที่แถวที่พัง (map ด้วย item_index — payload.items เรียงตาม cart 1:1)
     * แล้ว TabMenu ไฮไลต์แถวนั้น + เลื่อนไปให้เห็น + ขึ้นปุ่มนำออกแล้วส่งต่อ
     * ไม่มี toast ในเคสนี้ (ดู flagUnavailable) และตะกร้าไม่หุบจนกว่าจะส่งสำเร็จ
     * — ไม่งั้นลูกค้าจะไม่รู้ว่าต้องเอาอะไรออก แล้วกดส่งวนไม่จบ
     */
    async onSendOrder() {
      if (this.cart.length === 0 || this.sendingOrder) return;

      // ล้างธงรอบก่อน — ผลลัพธ์รอบนี้เท่านั้นที่นับ (ลูกค้าอาจแก้ของที่พังไปแล้ว)
      this.clearUnavailableFlags();

      const payload = {
        items: this.cart.map(c => {
          const item = {
            product_id: c.variant_id || c.product_id,
            quantity: c.quantity,
          };
          if (c.remark && c.remark.trim()) item.remark = c.remark.trim();
          if (Array.isArray(c.modifier_option_ids) && c.modifier_option_ids.length) {
            item.modifier_option_ids = c.modifier_option_ids;
          }
          return item;
        }),
      };

      // โปรซื้อครบ: reward ที่ลูกค้ากดรับใน cart → ส่งไปให้ BE materialize ตอนสร้างออร์เดอร์
      if (this.promotionClaims.length) {
        payload.promotion_claims = this.promotionClaims;
      }

      this.sendingOrder = true;
      try {
        await ServiceOrders.create(this.$route.params.token, payload);
        this.cart = [];
        this.promotionClaims = [];
        // ไม่ refreshRewards ที่นี่ — orders tab ไม่ใช้ promotion/loyalty; badge จะอัปเดตเองตอนเข้าแท็บ
        // menu/bill (ทั้งคู่ relay @rewards ขึ้นมา) → ไม่ต้องยิง promotion+loyalty เพิ่มหลังส่ง
        this.pushToast({
          message: this.$t('Order sent to kitchen!'),
          variant: 'success',
          icon: 'fa-solid fa-paper-plane',
        });
        // Jumping to the orders tab unmounts TabMenu (and the cart drawer
        // with it). TabOrders will fetch /my-order on mount so the new
        // queued items show immediately.
        this.activeTab = 'orders';
      } catch (err) {
        // Session expired mid-order → bounce to the public preview rather
        // than leaving the customer tapping a Send button that can't work.
        if (isServiceSessionInvalid(err)) {
          this.goToPreview();
          return;
        }
        if (isItemUnavailableError(err)) {
          this.flagUnavailable(err);
          return;
        }
        this.pushToast({
          message: this.$t('Unable to place order, please notify staff'),
          variant: 'error',
          icon: 'fa-solid fa-triangle-exclamation',
        });
      } finally {
        this.sendingOrder = false;
      }
    },

    /**
     * ติดธง `_unavailable` ให้แถวในตะกร้าที่ server ตีกลับ — ตะกร้าไม่ถูกล้าง
     *
     * ตั้งใจ **ไม่มี toast**: ตะกร้ายังเปิดค้างอยู่ (sendOrder ไม่หุบจนกว่าจะสำเร็จ)
     * แล้ว TabMenu เลื่อนไปแถวที่ไฮไลต์ให้เอง — toast ซ้อนมาอีกชั้นคือ noise
     * ที่บังของจริง แถมหายไปก่อนลูกค้าอ่านทัน
     */
    flagUnavailable(err) {
      const grouped = groupByItemIndex(parseUnavailableItems(err));

      this.cart = this.cart.map((item, index) => ({
        ...item,
        _unavailable: grouped.get(index) || null,
      }));

      // อยู่แท็บอื่นตอนกดส่ง → พากลับมาที่เมนูเพื่อให้เห็นแถวที่ถูก mark
      this.activeTab = 'menu';
      this.unavailableSeq += 1;
    },

    clearUnavailableFlags() {
      if (!this.hasUnavailable) return;
      this.cart = this.cart.map(item => ({ ...item, _unavailable: null }));
    },

    /**
     * "นำออกแล้วส่งต่อ" — ตัดแถวที่พังทิ้งแล้วยิงซ้ำให้เลย จบใน 1 แตะ
     * ถ้าตัดแล้วตะกร้าว่าง ก็แค่ล้าง ไม่ต้องยิง
     */
    async onRemoveUnavailable() {
      // ไม่มี toast ยืนยัน — แถวหายไปต่อหน้าในตะกร้าที่เปิดอยู่แล้ว (เห็นชัดกว่า toast)
      // และถ้าส่งผ่าน จะมี toast "ส่งเข้าครัวแล้ว" ตามมาทันที ซ้อนกันสองอันคือ noise
      this.cart = this.cart.filter(i => !(i._unavailable && i._unavailable.length));

      if (this.cart.length) await this.onSendOrder();
    },

    // --- Reward discoverability ------------------------------------------
    // remaining ของ tier "รวมตะกร้าสด" (สูตรเดียวกับ ServicePromotionPanel.remainingFor)
    tierRemaining(t) {
      const sentBase = Number(t.base) || 0;
      const cartContribution = (t.qualifying_scope === 'bill' && t.condition_type === 'spend')
        ? (Number(this.cartTotal) || 0)
        : 0;
      if (cartContribution === 0) return Math.max(0, Number(t.remaining) || 0);
      return Math.max(0, (Number(t.threshold) || 0) - (sentBase + cartContribution));
    },
    // map promotion+loyalty (relay จาก TabMenu/TabBill ผ่าน @rewards) → state ที่ขับ badge/CTA
    applyPromotion(d) {
      const x = d || {};
      this.rewardTiers = x.tiers || [];
      this.rewardTierMode = x.promotion_tier_mode || 'single';
      this.rewardClaimedIds = (x.claimed_tier_ids || []).map(Number);
    },
    applyLoyaltyCount(d) {
      const x = d || {};
      this.loyaltyRedeemable = (x.enabled && x.has_customer)
        ? (x.rewards_affordable || []).filter(r => r.reward_in_stock !== false).length
        : 0;
    },
    // TabMenu/TabBill ส่ง promotion+loyalty ที่ bundled มากับ /menu, /bills → อัปเดต badge โดยไม่ยิงเพิ่ม
    onRewards({ promotion, loyalty } = {}) {
      this.applyPromotion(promotion);
      this.applyLoyaltyCount(loyalty);
    },
    // --- Generic notification surface ------------------------------------
    onNotify({ message, variant = 'success', icon }) {
      this.pushToast({ message, variant, icon });
    },
    pushToast(t) {
      const id = ++this._toastSeq;
      this.toasts.push({ id, ...t });
      setTimeout(() => this.dismissToast(id), 3500);
    },
    dismissToast(id) {
      this.toasts = this.toasts.filter(t => t.id !== id);
    },
  },
};
</script>

<style scoped>
.service-shell {
  font-size: 0.95rem;
  --header-h: 3rem;
}
/*
  Collapsing header: on scroll the subtitle folds away and the bar
  shrinks, freeing vertical space for the menu. `--header-h` drops too,
  so the menu's sticky pill bar and the toast stack (both anchored to it)
  ride up in step with the header. Hysteresis lives in the scroll handler.
*/
.service-shell.is-scrolled {
  --header-h: 2.25rem;
}
.service-shell > header {
  min-height: var(--header-h);
  transition: min-height 0.25s ease;
}
/* Subtitle folds to zero height (not just opacity) so it gives its space
   back to the menu rather than leaving a gap. */
.header-sub {
  max-height: 1.2rem;
  overflow: hidden;
  opacity: 0.75;
  transition: max-height 0.25s ease, opacity 0.2s ease, margin 0.25s ease;
}
.service-shell.is-scrolled .header-sub {
  max-height: 0;
  opacity: 0;
}
.pb-safe {
  padding-bottom: env(safe-area-inset-bottom, 0);
}

/* --- Customer toasts ----------------------------------------------------
   Pill-shaped, centred under the header. The container itself is
   pointer-events: none so taps fall through to the menu underneath when
   no toast is showing; individual toasts re-enable pointer events.
*/
.service-toasts {
  position: fixed;
  top: calc(var(--header-h, 4.75rem) + 0.5rem);
  left: 0;
  right: 0;
  z-index: 1080;
  display: flex;
  justify-content: center;
  padding: 0 1rem;
  pointer-events: none;
}
.service-toast {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.15rem;
  border-radius: 999px;
  background: #fff;
  color: #2b2b2b;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.3;
  max-width: 100%;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.service-toast__icon { font-size: 1rem; flex-shrink: 0; }
.service-toast__msg {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
.service-toast--success {
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: #fff;
}
.service-toast--error {
  background: linear-gradient(135deg, #dc3545 0%, #ef5e6c 100%);
  color: #fff;
}

/* Pop-in: gentle overshoot so the chip feels like it "lands" in place.
   Leave: quick fade upward so a fast-tapping customer isn't kept waiting. */
.toast-pop-enter-active {
  transition: opacity 0.32s ease, transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.toast-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.toast-pop-enter-from {
  opacity: 0;
  transform: translateY(-16px) scale(0.92);
}
.toast-pop-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.toast-pop-move { transition: transform 0.25s ease; }

/* --- First-paint boot screen -------------------------------------------
   Full-screen branded loader shown on first visit while the session is
   verified. Warm orange wash + a softly breathing utensils mark and a
   three-dot pulse — deliberately content-less (no skeleton) so a bogus
   token never flashes a fake session before the redirect to preview.
*/
.service-boot {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Soft brand gradient instead of a flat fill so it feels intentional. */
  background: linear-gradient(160deg, #ff8a3c 0%, #e96c15 55%, #d65f0d 100%) !important;
}
.service-boot__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
}
.service-boot__logo {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  animation: boot-breathe 2.4s ease-in-out infinite;
}
.service-boot__brand {
  margin-top: 1.1rem;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: 0.3px;
}
.service-boot__dots {
  display: flex;
  gap: 0.4rem;
  margin-top: 1.1rem;
}
.service-boot__dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  animation: boot-dot 1.2s ease-in-out infinite;
}
.service-boot__dots span:nth-child(2) { animation-delay: 0.15s; }
.service-boot__dots span:nth-child(3) { animation-delay: 0.3s; }
.service-boot__hint {
  margin-top: 0.85rem;
  font-size: 0.85rem;
  opacity: 0.85;
}
@keyframes boot-breathe {
  0%, 100% { transform: scale(1); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.25); }
  50%      { transform: scale(1.06); box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22), inset 0 0 0 1px rgba(255, 255, 255, 0.4); }
}
@keyframes boot-dot {
  0%, 100% { opacity: 0.35; transform: translateY(0); }
  40%      { opacity: 1; transform: translateY(-5px); }
}
/* Fade the whole overlay out once the session is confirmed. */
.boot-fade-leave-active { transition: opacity 0.45s ease; }
.boot-fade-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .service-boot__logo,
  .service-boot__dots span { animation: none; }
}
</style>
